import { useNavigation, useRoute } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripePaymentElementOptions } from '@stripe/stripe-js';
import { useFormikContext } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  TouchableOpacity,
  ScrollView,
} from 'react-native';

import { Alert } from '@components/Alert/index.web';
import Avatar from '@components/Avatar/Avatar';
import CachedImage from '@components/CachedImage/CachedImage';
import { HeaderGoBack } from '@components/HeaderGoBack/HeaderGoBack';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { TeamPlanConfirmModal } from '@components/Modals/TeamPlanConfirmModal';
import { WatchedPaymentElement } from '@components/Payments/WatchedPaymentElement';
import { removeProjectLimitSnackBarNextTime } from '@components/Projects/ProjectForm.web';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { PlanItemWeb } from '@components/shared/PlanItemWeb';
import { Spinner } from '@components/shared/Spinner';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  GetTeamBillingDocument,
  GetTeamDocument,
  ListTeamsDocument,
  LocalFile,
  MemberAttributes,
  Price,
  Product,
  TeamRole,
  useCreateStripeSubscriptionMutation,
  useCreateTeamMutation,
  useListProductsQuery,
  useListTeamsQuery,
  useUpdatePaymentMethodMutation,
} from '@graphql/generated';
import useFileProcessor from '@hooks/useFileProcessor';
import useMe from '@hooks/useMe';
import { useTeamBillingFromQuery } from '@hooks/useTeamBillingFromQuery';
import { useTeamFromQuery } from '@hooks/useTeamFromQuery';
import { FormValues } from '@navigation/payments/create-subscription-stack';
import { BillingCycle, PlanName } from '@root/types';
import {
  MIXPANEL_EVENTS,
  MOBILE_PLAN_NAME,
  WEB_PLAN_NAME,
} from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import theme from '@themes/theme';
import { logEvent } from '@utils/eventLogger';
import { mapLocalFilesToDocuments } from '@utils/fileUtils';
import { buildCheckoutTeamAttributes } from '@utils/teams/deferredTeamSubscription';
import {
  calculateAnnualSavingPercentage,
  calculateSubtotalCost,
  maskEmail,
} from '@utils/teams/pricing';
import {
  formatCardExpiration,
  getTrialingDaysLeft,
  isCardExpired,
} from '@utils/teams/subscription';

import { SubscriptionSummary } from './components.web/SubscriptionSummary.web';
import { SubscriptionCreatedModal } from './SubscriptionCreateModal';

const createPlanItem =
  (
    displayPlan: PlanName | null,
    setPlan: (plan: PlanName) => void,
    setDisplayPlan: React.Dispatch<React.SetStateAction<PlanName | null>>
  ) =>
  (product: Product) => (
    <Box flexDirection='row' alignItems='center'>
      <Box mr='xs'>
        <RadioButton
          isSelected={displayPlan === product.id}
          onPress={() => {
            setPlan(product.id);
            setDisplayPlan(product.id);
          }}
        />
      </Box>
      <Box mr='xs'>
        <Icon
          name={
            product?.name?.toLowerCase()?.includes(MOBILE_PLAN_NAME)
              ? 'Zap'
              : 'Star'
          } // TODO get this from metadata?
          backgroundColor={
            product?.name?.toLowerCase()?.includes(MOBILE_PLAN_NAME)
              ? 'orange'
              : 'purple'
          } // TODO get this from metadata?
          color='background'
        />
      </Box>
      <Text variant='labelEmphasized' mr='xs'>
        {product.name}
      </Text>
      <Box>
        <Icon name='HelpCircle' color='grey04' />
      </Box>
    </Box>
  );

export const CreateSubscription: React.FC<{ externalNavigate?: any }> = ({
  externalNavigate,
}) => {
  const [loading, setLoading] = useState(false);
  const { me } = useMe();
  const { trackEvent } = useMixpanel();
  const { data } = useListTeamsQuery({
    fetchPolicy: 'cache-only',
    variables: { includePending: true },
  });
  const { listTeams } = data || {};
  const navigation = useNavigation();
  const navigator = externalNavigate || navigation;
  const { t } = useTranslation();
  const { data: listProductsData } = useListProductsQuery();

  const {
    setUpgradeTeamPlanOpen,
    setSubscriptionCreatedModalOpen,
    setSubscriptionCreatedTeamUUId,
  } = useWebDrawer();

  const route = useRoute();

  const { teamUUId } = route?.params || {
    teamUUId: '',
  };

  const teamQuery = useTeamFromQuery({
    teamUUId,
    includePending: true,
  });
  const teamDetail = teamQuery?.team;

  // Billing data is split out of listTeams (TA-9873) and fetched separately
  const { billingByTeamId, loading: billingLoading } = useTeamBillingFromQuery({
    includePending: true,
  });
  const teamBilling = teamDetail?.id
    ? billingByTeamId[teamDetail.id]
    : undefined;
  let subscriptionDetails = teamBilling?.subscriptionDetails;

  const [teamId, setTeamId] = useState<string>('');
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (teamDetail?.id) {
      setTeamId(teamDetail?.id);
    }
  }, [teamDetail?.id]);

  const hasSavedPaymentMethod = !!subscriptionDetails?.paymentCardLast4;

  const trialingDaysLeft = getTrialingDaysLeft(
    subscriptionDetails?.subscriptionStatus,
    subscriptionDetails?.expirationDate
  );

  const [
    createStripeSubscription,
    {
      data: createStripeSubscriptionData,
      error: createStripeSubscriptionError,
    },
  ] = useCreateStripeSubscriptionMutation({
    refetchQueries: [
      { query: GetTeamBillingDocument, variables: { includePending: true } },
      // Deferred team creation (TA-10009): the mutation may create the team,
      // so the teams list must be refreshed too
      { query: ListTeamsDocument, variables: { includePending: true } },
    ],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (!createStripeSubscriptionData) return;
    removeProjectLimitSnackBarNextTime(me?.id, '0');
    removeProjectLimitSnackBarNextTime(me?.id, teamId);
  }, [createStripeSubscriptionData]);

  useEffect(() => {
    if (!createStripeSubscriptionError) return;
    setLoading(false);
  }, [createStripeSubscriptionError]);

  const { listProducts } = listProductsData || {};
  const stripe = useStripe();
  const elements = useElements();

  const [showError, setShowError] = useState(false);
  const [_confirmDiscardModal, _setConfirmDiscardModal] = useState(false);
  const [payment, setPayment] = useState(elements?.getElement('payment'));

  const [updatePaymentMethod, { error: updatePaymentMethodError }] =
    useUpdatePaymentMethodMutation({
      refetchQueries: [
        { query: GetTeamBillingDocument, variables: { includePending: true } },
        {
          query: GetTeamDocument,
          variables: { id: teamDetail?.uuid, includePending: true },
        },
      ],
    });

  // Deferred team creation (TA-10009): the team is created at subscription
  // time so updatePaymentMethod has a real teamId to attach to
  const [createTeam] = useCreateTeamMutation();

  useEffect(() => {
    if (!updatePaymentMethodError) return;
    setLoading(false);
  }, [updatePaymentMethodError]);

  const personalTeam = listTeams?.find((t) => t?.personal);

  type PlanWithPrices = {
    [id: Product['id']]: {
      perMonth: Price['amount'];
      perYear: Price['amount'];
    };
  };
  const stripeOptions: StripePaymentElementOptions = useMemo(
    () => ({
      layout: 'tabs',
      paymentMethodOrder: ['card', 'us_bank_account'],
    }),
    []
  );

  // convert Stripe products in to an easier to use format
  const plans: PlanWithPrices =
    listProducts?.reduce((acc, product) => {
      const prices = {
        perMonth:
          product?.prices?.find((price) => price?.interval === 'month')
            ?.amount || 0,
        perYear:
          product?.prices?.find((price) => price?.interval === 'year')
            ?.amount || 0,
      };
      return { ...acc, [product['id']]: prices };
    }, {}) || {};

  const { values, isValid, setFieldValue, setFieldTouched } =
    useFormikContext<FormValues>();
  const { users, plan, priceId, billingCycle, name, logo } = values;

  const isPersonal = teamDetail?.personal ?? false;

  // Pass null when expiry fields are missing — isCardExpired treats the
  // literal "undefined/undefined" string as a valid, non-expired card
  const isSavedPaymentMethodExpired = isCardExpired(
    subscriptionDetails?.paymentCardExpMonth &&
      subscriptionDetails?.paymentCardExpYear
      ? `${subscriptionDetails.paymentCardExpMonth}/` +
          `${subscriptionDetails.paymentCardExpYear}`
      : null
  );

  const [useNewPaymentMethod, setUseNewPaymentMethod] = useState(
    !hasSavedPaymentMethod || isSavedPaymentMethodExpired
  );

  // Billing now arrives async (separate getTeamBilling request) — re-derive
  // the default payment method selection once it loads
  useEffect(() => {
    setUseNewPaymentMethod(
      !hasSavedPaymentMethod || isSavedPaymentMethodExpired
    );
  }, [hasSavedPaymentMethod, isSavedPaymentMethodExpired]);

  const filteredProducts = listProducts?.filter((plan) =>
    plan?.name
      .toLowerCase()
      .includes(Platform.OS === 'web' ? WEB_PLAN_NAME : MOBILE_PLAN_NAME)
  );

  const [displayPlan, setDisplayPlan] = React.useState(
    !subscriptionDetails && (filteredProducts?.length ?? 0) > 0
      ? filteredProducts![0].id
      : plan
  );

  const usersAndMe = () => {
    return users;
  };

  const sortedUsers = [...(usersAndMe() || [])]?.sort((u) =>
    u?.role !== TeamRole?.Owner ? 1 : -1
  );

  const setPlan = (plan: PlanName) => {
    const product = listProducts?.find((p) => p?.id === plan);
    setFieldValue('plan', plan);
    product && setFieldValue('planName', product?.name);

    const price = product?.prices?.find(
      (price) => price?.interval === billingCycle
    );
    price && setFieldValue('priceId', price?.externalId);
    price && setFieldValue('price', subtotalCost());

    if (!personalTeam) return;

    if (!product?.allowMembers) {
      setFieldValue('teamId', personalTeam?.id);
    } else {
      setFieldValue('teamId', null);
    }
  };

  const setBillingCycle = (billingCycle: BillingCycle) => {
    setFieldValue('billingCycle', billingCycle);

    const product = listProducts?.find((p) => p?.id === displayPlan);
    const price = product?.prices?.find((pr) => pr?.interval === billingCycle);

    if (price) {
      setFieldValue('priceId', price.externalId);

      const multiplier = !product?.allowMembers ? 1 : Math.max(users.length, 1);
      const planCost =
        billingCycle === 'year'
          ? plans?.[displayPlan]?.perYear
          : plans?.[displayPlan]?.perMonth;
      setFieldValue('price', Math.ceil(planCost * multiplier));
    }
  };

  useEffect(() => {
    if (!displayPlan || !listProducts?.length) return;

    const product = listProducts.find((p) => p?.id === displayPlan);
    const price = product?.prices?.find((p) => p?.interval === billingCycle);

    if (price && price.externalId !== priceId) {
      setFieldValue('priceId', price.externalId);
    }

    if (product) {
      const multiplier = !product.allowMembers ? 1 : Math.max(users.length, 1);
      const planCost =
        billingCycle === 'year'
          ? plans?.[displayPlan]?.perYear
          : plans?.[displayPlan]?.perMonth;
      const newPrice = Math.ceil(planCost * multiplier);
      if (planCost && values.price !== newPrice) {
        setFieldValue('price', newPrice);
      }
    }
  }, [displayPlan, billingCycle, users.length, listProducts?.length, priceId]);

  const selectedProduct = listProducts?.find((p) => p?.id === displayPlan);

  const subtotalCost = () =>
    calculateSubtotalCost({
      plans,
      displayPlan,
      billingCycle,
      memberCount: users.length,
      allowMembers: selectedProduct?.allowMembers ?? false,
    });
  const subtotalValue = subtotalCost();

  const subtotalLabel = `$${(subtotalValue / 100)?.toFixed(2)}/${
    billingCycle === 'year' ? 'yr' : 'mo'
  }`;

  const originalCost = () => {
    if (!listProducts?.length || !displayPlan) return 0;
    let multiplier = Math.max(users.length, 1);
    if (
      teamId &&
      teamDetail &&
      subscriptionDetails?.subscriptionStatus === 'active'
    )
      multiplier = users.length - teamDetail.users.length;
    const selectedPlan = plans?.[displayPlan];
    return billingCycle === 'year'
      ? selectedPlan?.perMonth * 12 * multiplier
      : 1;
  };

  const teamIcon = () => {
    if (isPersonal) {
      return (
        <Box
          width={24}
          height={24}
          borderRadius='xxs'
          backgroundColor='greenSecondary'
          justifyContent='center'
          alignItems='center'>
          <Icon variant='s' name='Office' color='white' />
        </Box>
      );
    } else {
      return <Icon name='TT24' variant='l' color='textPrimary' />;
    }
  };

  const originalCostLabel = `$${(originalCost() / 100)?.toFixed(2)}`;

  const percentageSaved =
    billingCycle === 'year'
      ? calculateAnnualSavingPercentage(
          displayPlan ? plans?.[displayPlan] : undefined
        )
      : undefined;

  const selectedTeamName = undefined;
  const currentTeam = undefined;

  const planItem = createPlanItem(displayPlan, setPlan, setDisplayPlan);

  const teamPlan = listProducts?.find((p) => p?.allowMembers);
  const teamPlanSelected = teamPlan?.id;

  const switchToTeamPlan = () => {
    if (!teamPlan) return;
    setDisplayPlan(teamPlan?.id);
    setPlan(teamPlan?.id);
  };

  const useNewCheckoutLayout = Platform.OS === 'web';

  const { processFiles } = useFileProcessor();

  const [isSubscriptionUpgraded, setIsSubscriptionUpgraded] =
    useState<boolean>(false);
  const [isPaymentProcessing, setIsPaymentProcessing] =
    useState<boolean>(false);

  const [isPaymentFailed, setIsPaymentFailed] = useState<boolean>(false);

  const handlePaymentReady = useCallback(() => {
    const xPayment = elements?.getElement('payment');
    if (xPayment && xPayment !== payment) {
      setPayment(xPayment);
    }
  }, [elements, payment]);

  const submitForm = async () => {
    if (!elements || !stripe || !priceId || !me) return;
    setLoading(true);
    setIsPaymentFailed(false);
    setIsPaymentProcessing(true);

    const members: MemberAttributes[] = users.map((u) => ({
      userId: u.id,
      role: u.role ?? u.teamRoles?.[0],
    }));
    const deferredTeamMembers = values.teamMembers ?? [];

    const isDeferredNewTeam = !(teamId !== '' && teamId !== null && teamDetail);
    let subscriptionTeamId = teamId;
    let subscribedTeamUUId = teamUUId;

    const failSubmit = () => {
      showSubscripeFail();
      setIsPaymentFailed(true);
      setIsPaymentProcessing(false);
      setLoading(false);
    };

    try {
      // Validate the payment form before creating anything, so an invalid
      // card doesn't leave a team behind
      if (useNewPaymentMethod) {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          failSubmit();
          return;
        }
      }

      // Deferred team creation (TA-10009): the team is created only now, at
      // subscription time, because updatePaymentMethod needs a teamId. Must
      // run regardless of payment-method selection — a saved payment method
      // still needs the team to exist.
      if (isDeferredNewTeam) {
        let avatarBlobId = undefined;
        if (values.logo?.__typename === 'LocalFile') {
          await processFiles(mapLocalFilesToDocuments([values.logo]))
            .then(async (response) => {
              avatarBlobId = response[0]?.blobId;
            })
            .catch((err) => {
              alert(err);
            });
        }

        const { data: createTeamData } = await createTeam({
          variables: {
            attributes: buildCheckoutTeamAttributes({
              values,
              fallbackName: me.firstName,
              members,
              deferredMembers: deferredTeamMembers,
              avatarBlobId,
            }),
          },
        });
        const createdTeam = createTeamData?.createTeam;
        if (!createdTeam?.id) {
          failSubmit();
          return;
        }
        // TA-10886: this is the only createTeam left on the web signup path
        // (registration no longer creates the team), so the funnel's
        // team_created event has to be emitted here or teams created through
        // registration -> checkout disappear from it.
        trackEvent(MIXPANEL_EVENTS.TEAM_CREATED, {
          team_id: createdTeam.uuid,
        });
        subscriptionDetails = createdTeam.subscriptionDetails;
        subscriptionTeamId = createdTeam.id;
        subscribedTeamUUId = createdTeam.uuid ?? '';
        setTeamId(createdTeam.id);
      }

      if (useNewPaymentMethod) {
        const { paymentMethod } = await stripe.createPaymentMethod({
          elements,
          params: {
            billing_details: {
              email: me?.email,
            },
          },
        });

        if (paymentMethod) {
          const teamUUIdForRefetch = teamDetail?.uuid || subscribedTeamUUId;
          await updatePaymentMethod({
            variables: {
              teamId: subscriptionTeamId,
              paymentMethodToken: paymentMethod.id,
            },
            refetchQueries: [
              {
                query: GetTeamBillingDocument,
                variables: { includePending: true },
              },
              ...(teamUUIdForRefetch
                ? [
                    {
                      query: GetTeamDocument,
                      variables: {
                        id: teamUUIdForRefetch,
                        includePending: true,
                      },
                    },
                  ]
                : []),
            ],
          });
        }
      }

      const isonTrial = subscriptionDetails?.isSubscriptionOnTrial;

      if (!isonTrial) {
        const { data: subscriptionData } = await createStripeSubscription({
          variables: {
            stripeSubscriptionAttributes: {
              name: plan || '',
              teamId: subscriptionTeamId ?? '',
              quantity: members.length + deferredTeamMembers.length,
              priceId: priceId,
            },
          },
        });
        const { createStripeSubscription: subscriptionResponse } =
          subscriptionData || {};

        if (!subscriptionResponse) {
          failSubmit();
          return;
        }

        if (Platform.OS === 'web') {
          const intentType = subscriptionResponse.intentType
            ?.toLowerCase()
            .trim();
          if (intentType === 'setup_intent' || intentType === 'setup') {
            logEvent({
              name: 'trial_started',
              params: {
                trial_plan: selectedProduct?.name ?? plan ?? 'unknown',
                trial_duration_days: 30,
                source_page: window.location.pathname,
              },
            });
            trackEvent(MIXPANEL_EVENTS.TRIAL_STARTED, {
              plan_type: selectedProduct?.name ?? plan ?? 'unknown',
              billing_period: billingCycle,
              team_id: subscriptionResponse.team?.id || subscriptionTeamId,
            });
          } else if (intentType === 'payment_intent') {
            logEvent({ name: 'purchase' });
            if (subscriptionDetails?.isSubscriptionActive) {
              // Upgrading an existing active subscription to a different plan
              trackEvent(MIXPANEL_EVENTS.PLAN_UPGRADED, {
                plan_type: selectedProduct?.name ?? plan ?? 'unknown',
                billing_period: billingCycle,
                team_id: subscriptionResponse.team?.id || subscriptionTeamId,
              });
            } else {
              trackEvent(MIXPANEL_EVENTS.SUBSCRIPTION_STARTED, {
                plan_type: selectedProduct?.name ?? plan ?? 'unknown',
                billing_period: billingCycle,
                team_id: subscriptionResponse.team?.id || subscriptionTeamId,
              });
            }
          }
        }
      } else if (Platform.OS === 'web') {
        // Trial user converting to a paid plan (backend converts the trial)
        trackEvent(MIXPANEL_EVENTS.PLAN_UPGRADED, {
          plan_type: selectedProduct?.name ?? plan ?? 'unknown',
          billing_period: billingCycle,
          team_id: subscriptionTeamId,
          from_trial: true,
        });
      }
    } catch (error) {
      // Payment-method attach or subscription creation threw — roll back the
      // team this submit created and surface the failure
      Sentry.captureException(error);
      failSubmit();
      return;
    }

    setIsPaymentProcessing(false);
    setIsPaymentFailed(false);
    setLoading(false);

    if (Platform.OS === 'web') {
      navigator.navigate('my-account', {
        screen: 'tabs',
        params: {
          screen: 'my-profile',
          params: {
            screen: 'home',
          },
        },
      });
      setUpgradeTeamPlanOpen(false);
      setSubscriptionCreatedTeamUUId(subscribedTeamUUId);
      setSubscriptionCreatedModalOpen(true);
    } else {
      setIsSubscriptionUpgraded(true);
    }
  };
  const showSubscripeFail = () => {
    if (Platform.OS === 'web') setShowError(true);
    else {
      Alert.alert(
        t('models:teams.subscriptions.failureAlert.title'),
        t('models:teams.subscriptions.failureAlert.message')
      );
    }
  };

  useEffect(() => {
    const product = listProducts?.find((p) => p.id === displayPlan);
    const price = product?.prices.find(
      (price) => price.interval === billingCycle
    );
    price && setFieldValue('priceId', price.externalId);
    price && setFieldValue('price', subtotalCost());
  }, [billingCycle, subtotalValue]);

  useEffect(() => {
    if (!payment) return;

    payment.on('change', (e) => {
      setFieldValue('paymentComplete', e.complete);
    });

    return () => {
      // Stripe documentation indicated this should be done, even though TS is yelling about it
      (payment as any)?.removeAllListeners();
    };
  }, [payment]);

  const expiration = formatCardExpiration(
    subscriptionDetails?.paymentCardExpMonth,
    subscriptionDetails?.paymentCardExpYear,
    isSavedPaymentMethodExpired
  );

  useEffect(() => {
    if (!useNewPaymentMethod) {
      setFieldValue('paymentComplete', false);
    }
  }, [useNewPaymentMethod]);

  useEffect(() => {
    if (Boolean(teamDetail?.id) && !isPersonal) {
      setFieldValue('name', teamDetail?.name);
      setFieldValue('teamId', teamDetail?.id);
    }
  }, [plan]);

  useEffect(() => {
    if (teamDetail?.users && teamDetail.users.length > 1) {
      setFieldValue('users', teamDetail.users);
    }
  }, [teamDetail?.users?.length]);

  useEffect(() => {
    if (!filteredProducts) return;

    const product = filteredProducts[0];
    product && setDisplayPlan(product.id);
    product && setFieldValue('plan', product.id);
    product && setFieldValue('planName', product.name);
  }, [filteredProducts?.length]);

  const percentageSavedWeb = (product: Product) => {
    const selectedPlan = plans?.[product.id];
    if (!selectedPlan) {
      return undefined;
    }

    const monthPerYear = selectedPlan?.perMonth * 12;
    const annualPrice = selectedPlan?.perYear;
    const percentage = ((monthPerYear - annualPrice) / monthPerYear) * 100;
    return Math.trunc(percentage);
  };

  let upToSave = 20;
  filteredProducts?.forEach((p) => {
    const saved = percentageSavedWeb(p) ?? 0;
    if (upToSave <= saved) upToSave = saved;
  });

  const changed = () => {
    if (teamDetail) {
      if (useNewPaymentMethod && values.paymentComplete) return true;
      if (!subscriptionDetails && plan) return true;
      if (
        subscriptionDetails &&
        subscriptionDetails?.subscriptionInterval !== plan
      )
        return true;
      if (teamDetail.users.length != users.length) return true;
      const oldUsersIds = teamDetail.users.map((u) => u.id);

      for (const a of users) {
        if (!oldUsersIds.includes(a.id)) return true;
      }
    }
    return false;
  };

  const { setIdTeamDelete: _setIdTeamDelete } = useWebDrawer();

  // Billing arrives in a separate getTeamBilling request (TA-9873) — show a
  // loading state for existing teams until it lands
  if (!!teamUUId && billingLoading && !teamBilling) {
    return (
      <Box
        flex={1}
        justifyContent='center'
        alignItems='center'
        backgroundColor='white'
        padding='l'>
        <ActivityIndicator testID='BillingActivityIndicator' />
      </Box>
    );
  }

  if (Platform.OS === 'web') {
    return (
      <>
        {showError && (
          <ConfirmModal
            isAlert={true}
            showModal={showError}
            onClose={() => setShowError(false)}
            onPress={() => setShowError(false)}
            buttonText='     OK     '
            title={t('models:teams.subscriptions.failureAlert.title')}
            message={t('models:teams.subscriptions.failureAlert.message')}
          />
        )}

        {showConfirm && (
          <TeamPlanConfirmModal
            showModal={showConfirm}
            onClose={() => {
              setShowConfirm(false);
              setUpgradeTeamPlanOpen(false);
            }}
            onPress={() => {
              setShowConfirm(false);
            }}
            buttonText={t('shared:continue')}
            title={t('shared:subscriptionConfirmTitle')}
            message={t('shared:subscriptionConfirm')}
            cancelButtonText={t('shared:later')}
          />
        )}

        {isSubscriptionUpgraded && (
          <SubscriptionCreatedModal
            onClose={() => {
              setUpgradeTeamPlanOpen(false);
              setIsSubscriptionUpgraded(false);
            }}
          />
        )}
        <Box style={{ backgroundColor: 'white' }} height='100%'>
          {useNewCheckoutLayout ? (
            <Box backgroundColor='white' flexDirection='row' height='100%'>
              {/* Left Column - Subscription Summary */}
              <Box
                flex={1}
                backgroundColor='grey01'
                borderRightWidth={0}
                borderRightColor='grey02'>
                <SubscriptionSummary
                  teamName='Team Plan'
                  billingCycle={billingCycle}
                  onBillingCycleChange={setBillingCycle}
                  pricePerMember={
                    billingCycle === 'year'
                      ? plans?.[displayPlan]?.perYear / 12
                      : plans?.[displayPlan]?.perMonth
                  }
                  savePercentage={percentageSaved}
                  subtotal={subtotalValue}
                  tax={0}
                  total={subtotalValue}
                  onBack={() => setShowConfirm(true)}
                />
              </Box>

              {/* Right Column - Payment Method */}
              <Box flex={1} paddingHorizontal='l' paddingTop='xl'>
                <Box marginBottom='l'>
                  <Text
                    variant='labelLarge'
                    color='textPrimary'
                    marginBottom='m'>
                    {t('models:teams.subscriptions.paymentMethod')}
                  </Text>
                  {(subscriptionDetails?.subscriptionStatus ?? '') ===
                    'trialing' && (
                    <Text
                      mb='m'
                      variant='webBodySecondary'
                      color='textSecondary'>
                      {t(
                        'models:teams.subscriptions.subscriptionTrialMessage',
                        {
                          leftDays:
                            trialingDaysLeft +
                            (trialingDaysLeft !== '1' ? ' days' : ' day'),
                        }
                      )}
                    </Text>
                  )}
                </Box>

                {/* Payment Form */}
                <ScrollView
                  style={{ flex: 1 }}
                  contentContainerStyle={{ paddingBottom: theme.spacing.xs }}
                  showsVerticalScrollIndicator={false}>
                  <Box>
                    {isPaymentFailed && (
                      <Box
                        backgroundColor='alertRedMild'
                        borderRadius='xs'
                        padding='l'
                        marginBottom='l'>
                        <Text color='alertRed' variant='labelEmphasized'>
                          {t('models:payments.paymentFailed')}
                        </Text>
                      </Box>
                    )}
                    {isPaymentProcessing && (
                      <Box
                        marginVertical='l'
                        justifyContent='center'
                        alignItems='center'>
                        <Spinner size={156} />
                        <Text marginTop='xl' variant='labelLarge'>
                          Payment Processing
                        </Text>
                      </Box>
                    )}
                    {!isPaymentProcessing && hasSavedPaymentMethod && (
                      <Box>
                        <Box flexDirection='row' mb='m' alignItems='center'>
                          <RadioButton
                            testID='saved-payment-radio'
                            isSelected={
                              !useNewPaymentMethod &&
                              !isSavedPaymentMethodExpired
                            }
                            disabled={isSavedPaymentMethodExpired}
                            onPress={() => {
                              setUseNewPaymentMethod(false);
                            }}
                          />
                          <Text
                            variant='labelSmall'
                            testID='saved-payment-label'
                            ml='xs'>
                            Saved
                          </Text>
                        </Box>
                        {subscriptionDetails &&
                          (
                            subscriptionDetails?.paymentCardBrand ?? ''
                          ).includes('@') && (
                            <Box
                              flexDirection='row'
                              justifyContent='center'
                              alignItems='center'
                              mb='m'
                              width={400}>
                              <Icon
                                name='LinkPayment'
                                variant='l'
                                color='white'
                                marginLeft='m'
                              />
                              <Text
                                variant='labelSmall'
                                color='textSecondary'
                                ml='xxs'>
                                {`Link  ${maskEmail(
                                  subscriptionDetails?.paymentCardBrand ?? ''
                                )}`}
                              </Text>
                              <Box flex={1} />
                            </Box>
                          )}
                        {subscriptionDetails?.paymentCardLast4 && (
                          <Box
                            flexDirection='row'
                            testID='saved-payment-method'
                            justifyContent='center'
                            alignItems='center'
                            mb='m'
                            width={400}>
                            <Icon
                              name='Visa'
                              variant='xxl'
                              color='white'
                              marginLeft='m'
                            />
                            <Text
                              variant='labelSmall'
                              color='textSecondary'
                              testID='card-number-text'
                              ml='m'>
                              **** **** ****{' '}
                              {subscriptionDetails?.paymentCardLast4}
                            </Text>
                            <Text
                              variant='webBodySecondary'
                              testID='card-expiration-text'
                              color={
                                isSavedPaymentMethodExpired
                                  ? 'alertRed'
                                  : 'textSecondary'
                              }
                              ml='xxl'>
                              {expiration}
                            </Text>
                          </Box>
                        )}

                        <Box flexDirection='row' mb='m' alignItems='center'>
                          <RadioButton
                            disabled={isSavedPaymentMethodExpired}
                            isSelected={useNewPaymentMethod}
                            onPress={() => {
                              setUseNewPaymentMethod(true);
                            }}
                          />
                          <Text
                            variant='labelSmall'
                            testID='change-payment-label'
                            ml='xs'>
                            Change payment method
                          </Text>
                        </Box>
                      </Box>
                    )}
                    {useNewPaymentMethod && (
                      <Box
                        mb='l'
                        testID='new-payment-element-container'
                        style={{
                          display: isPaymentProcessing ? 'none' : undefined,
                        }}>
                        <WatchedPaymentElement
                          id='payment-element'
                          options={{
                            layout: 'tabs',
                            paymentMethodOrder: ['card', 'us_bank_account'],
                            fields: {
                              billingDetails: {
                                name: 'auto',
                                email: 'never',
                                address: 'auto',
                              },
                            },
                            business: {
                              name: 'Your Company Name',
                            },
                            wallets: {
                              applePay: 'auto',
                              googlePay: 'auto',
                            },
                            terms: { card: 'never' },
                            // NOTE: a hardcoded-hex `style` object previously
                            // lived here, but `style` is not a valid
                            // StripePaymentElementOptions key (Stripe styles
                            // elements via the Elements `appearance` API), so
                            // it was never applied — removed as dead config
                          }}
                          onReady={handlePaymentReady}
                        />
                      </Box>
                    )}
                  </Box>
                </ScrollView>

                {!isPaymentProcessing && (
                  <Box paddingBottom='xl'>
                    <Button
                      testID='submit-button'
                      variant='primary'
                      backgroundColor='greenSecondary'
                      btnTextColor='white'
                      borderWidth={0}
                      disabled={
                        isPaymentProcessing ||
                        (!hasSavedPaymentMethod && !values.paymentComplete) ||
                        loading ||
                        displayPlan == null
                      }
                      onPress={() => {
                        if (plan === null && displayPlan != null)
                          setPlan(displayPlan);
                        submitForm();
                      }}>
                      {t('models:payments.subscribe')}
                    </Button>

                    {/* Footer */}
                    <Box marginTop='l' alignItems='center'>
                      <Text
                        variant='metadata'
                        color='grey04'
                        textAlign='center'>
                        By subscribing, you authorize TaskTag to charge you
                        according to the terms until you cancel
                      </Text>
                      <Box
                        flexDirection='row'
                        marginTop='s'
                        alignItems='center'>
                        <Text variant='metadata' color='grey04'>
                          Powered by{' '}
                        </Text>
                        <Text
                          variant='metadata'
                          color='grey04'
                          fontWeight='600'>
                          stripe
                        </Text>
                        <Text variant='metadata' color='grey04'>
                          {' '}
                          |{' '}
                        </Text>
                        <Text variant='metadata' color='grey04'>
                          Terms
                        </Text>
                        <Text variant='metadata' color='grey04'>
                          {' '}
                          |{' '}
                        </Text>
                        <Text variant='metadata' color='grey04'>
                          Privacy
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box backgroundColor='white' maxWidth={560}>
              <Box flexDirection='row' marginBottom='s'>
                <Box flex={1} flexDirection='row'>
                  <Text
                    testID='subscription-header'
                    variant='heading2'
                    color='textPrimary'
                    textAlign='left'>
                    {t('models:profile.subscriptionPlans.subscriptionDetails')}
                  </Text>
                </Box>
                <Box alignItems='flex-end'>
                  <Box flexDirection='row' accessibilityLabel='Close'>
                    <Icon
                      name='X'
                      onPress={() => {
                        setUpgradeTeamPlanOpen(false);
                        setConfirmDiscardModal(true);
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              <Box mb='l'>
                <Text variant='labelLarge' mb='xs' color='textPrimary'>
                  Choose Plan
                </Text>
                <Text variant='webBodySecondary' mb='m' color='greenSecondary'>
                  Save up to {upToSave}%{' '}
                  <Text variant='webBodySecondary' color='textSecondary'>
                    when you subscribe Annually.
                  </Text>
                </Text>
                <Box flex={1}>
                  {filteredProducts?.map((product) => {
                    return (
                      <PlanItemWeb
                        key={'2' + product.id}
                        product={product}
                        isMonthly={false}
                        isSelected={
                          displayPlan === product.id && billingCycle === 'year'
                        }
                        onPress={() => {
                          if (
                            displayPlan === product.id &&
                            billingCycle === 'year'
                          ) {
                            setDisplayPlan(null);
                            setPlan(null);
                          } else {
                            setDisplayPlan(product.id);
                            setPlan(product.id);
                            setBillingCycle('year');
                          }
                        }}
                        percentageSaved={percentageSavedWeb(product)}
                        testID={`plan-option-${product.id}`}
                      />
                    );
                  })}
                  {filteredProducts?.map((product) => {
                    return (
                      <PlanItemWeb
                        key={'1' + product.id}
                        product={product}
                        isMonthly={true}
                        isSelected={
                          displayPlan === product.id && billingCycle === 'month'
                        }
                        disabled={
                          subscriptionDetails?.subscriptionInterval === 'year'
                        }
                        onPress={() => {
                          if (
                            displayPlan === product.id &&
                            billingCycle === 'month'
                          ) {
                            setDisplayPlan(null);
                            setPlan(null);
                          } else {
                            setDisplayPlan(product.id);
                            setPlan(product.id);
                            setBillingCycle('month');
                          }
                        }}
                        testID={`plan-option-${product.id}`}
                      />
                    );
                  })}
                </Box>
              </Box>

              <Box my='m'>
                <Text mb='m' variant='labelLarge' color='textPrimary'>
                  {teamDetail?.id ? 'Team' : 'Select Team'}
                </Text>
                <Box
                  flexDirection='row'
                  alignItems='center'
                  justifyContent='center'
                  borderColor='grey03'
                  backgroundColor='grey01'
                  borderWidth={1}
                  borderRadius='xs'
                  height={48}
                  mb='s'>
                  {teamDetail?.avatarFile && (
                    <CachedImage
                      image={teamDetail.avatarFile as unknown as LocalFile}
                      width={32}
                      height={32}
                      borderRadius={theme.spacing.xs}
                    />
                  )}
                  {!teamDetail?.avatarFile && name && logo && (
                    <CachedImage
                      image={logo as unknown as LocalFile}
                      height={32}
                      width={32}
                      borderRadius={theme.spacing.xs}
                    />
                  )}
                  {!teamDetail?.avatarFile && !logo && teamIcon()}
                  <Text
                    mx='xs'
                    variant='labelSmall'
                    color='textSecondary'
                    numberOfLines={1}>
                    {teamDetail?.name ?? name}
                  </Text>
                </Box>
              </Box>

              <Box mb='m'>
                <Text
                  mb='xs'
                  variant='labelLarge'
                  testID='payment-method-header'
                  color='textPrimary'>
                  {t('models:teams.subscriptions.paymentMethod')}
                </Text>
                {(subscriptionDetails?.subscriptionStatus ?? '') ===
                  'trialing' && (
                  <Text mb='m' variant='webBodySecondary' color='textSecondary'>
                    {t('models:teams.subscriptions.subscriptionTrialMessage', {
                      leftDays:
                        trialingDaysLeft +
                        (trialingDaysLeft !== '1' ? ' days' : ' day'),
                    })}
                  </Text>
                )}
              </Box>

              {/* Payment Form */}
              <Box>
                {isPaymentFailed && (
                  <Box
                    backgroundColor='alertRedMild'
                    borderRadius='xs'
                    padding='l'
                    marginBottom='l'>
                    <Text color='alertRed' variant='labelEmphasized'>
                      {t('models:payments.paymentFailed')}
                    </Text>
                  </Box>
                )}
                {isPaymentProcessing && (
                  <Box
                    marginVertical='l'
                    justifyContent='center'
                    alignItems='center'>
                    <Spinner size={156} />
                    <Text marginTop='xl' variant='labelLarge'>
                      Payment Processing
                    </Text>
                  </Box>
                )}
                {!isPaymentProcessing && hasSavedPaymentMethod && (
                  <Box>
                    <Box flexDirection='row' mb='m' alignItems='center'>
                      <RadioButton
                        testID='saved-payment-radio'
                        isSelected={
                          !useNewPaymentMethod && !isSavedPaymentMethodExpired
                        }
                        disabled={isSavedPaymentMethodExpired}
                        onPress={() => {
                          setUseNewPaymentMethod(false);
                        }}
                      />
                      <Text
                        variant='labelSmall'
                        testID='saved-payment-label'
                        ml='xs'>
                        Saved
                      </Text>
                    </Box>
                    {/*Link payment method*/}
                    {subscriptionDetails &&
                      (subscriptionDetails?.paymentCardBrand ?? '').includes(
                        '@'
                      ) && (
                        <Box
                          flexDirection='row'
                          justifyContent='center'
                          alignItems='center'
                          mb='m'
                          width={400}>
                          <Icon
                            name='LinkPayment'
                            variant='l'
                            color='white'
                            marginLeft='m'
                          />
                          <Text
                            variant='labelSmall'
                            color='textSecondary'
                            ml='xxs'>
                            {`Link  ${maskEmail(
                              subscriptionDetails?.paymentCardBrand ?? ''
                            )}`}
                          </Text>
                          <Box flex={1} />
                        </Box>
                      )}
                    {/*stripe payment method*/}
                    {subscriptionDetails?.paymentCardLast4 && (
                      <Box
                        flexDirection='row'
                        testID='saved-payment-method'
                        justifyContent='center'
                        alignItems='center'
                        mb='m'
                        width={400}>
                        <Icon
                          name='Visa'
                          variant='xxl'
                          color='white'
                          marginLeft='m'
                        />
                        <Text
                          variant='labelSmall'
                          color='textSecondary'
                          testID='card-number-text'
                          ml='m'>
                          **** **** **** {subscriptionDetails?.paymentCardLast4}
                        </Text>
                        <Text
                          variant='webBodySecondary'
                          testID='card-expiration-text'
                          color={
                            isSavedPaymentMethodExpired
                              ? 'alertRed'
                              : 'textSecondary'
                          }
                          ml='xxl'>
                          {expiration}
                        </Text>
                      </Box>
                    )}

                    <Box flexDirection='row' mb='m' alignItems='center'>
                      <RadioButton
                        disabled={isSavedPaymentMethodExpired}
                        isSelected={useNewPaymentMethod}
                        onPress={() => {
                          setUseNewPaymentMethod(true);
                        }}
                      />
                      <Text
                        variant='labelSmall'
                        testID='change-payment-label'
                        ml='xs'>
                        Change payment method
                      </Text>
                    </Box>
                  </Box>
                )}
                {/** Added display condition to keep the component in DOM but hide it on screen */}
                {useNewPaymentMethod && (
                  <Box
                    mb='l'
                    testID='new-payment-element-container'
                    style={{
                      display: isPaymentProcessing ? 'none' : undefined,
                    }}>
                    <WatchedPaymentElement
                      options={stripeOptions}
                      onReady={handlePaymentReady}
                    />
                  </Box>
                )}

                {!isPaymentProcessing && listProducts?.length && (
                  <Box
                    mb='l'
                    flexDirection='row'
                    flex={1}
                    style={{ paddingVertical: 32 }}
                    px='l'
                    borderColor='grey02'
                    borderWidth={1}
                    backgroundColor='grey01'
                    accessibilityLabel={t(
                      'models:teams.subscriptions.subtotal'
                    )}
                    alignItems='center'>
                    <Text variant='labelLarge' mb='xs' color='textPrimary'>
                      {t('models:teams.subscriptions.subtotal')}
                    </Text>

                    <Box flexDirection='row' flex={1} justifyContent='flex-end'>
                      {billingCycle === 'year' && (
                        <Text
                          color='grey04'
                          textDecorationStyle='solid'
                          textDecorationLine='line-through'
                          mr='xxs'>
                          {originalCostLabel}
                        </Text>
                      )}
                      <Text variant='labelLarge' color='textPrimary'>
                        {subtotalLabel}
                      </Text>
                    </Box>
                  </Box>
                )}
                {(!subscriptionDetails ||
                  subscriptionDetails?.isSubscriptionCanceled) && (
                  <Button
                    testID='submit-button'
                    variant='primary'
                    disabled={
                      isPaymentProcessing ||
                      (!hasSavedPaymentMethod && !values.paymentComplete) ||
                      loading ||
                      displayPlan == null
                    }
                    onPress={() => {
                      if (plan === null && displayPlan != null)
                        setPlan(displayPlan);
                      submitForm();
                    }}>
                    <Text color='background'>
                      {t('models:payments.subscribeNow')}
                    </Text>
                  </Button>
                )}
                {subscriptionDetails &&
                  !subscriptionDetails?.isSubscriptionCanceled && (
                    <Box flexDirection='row' flex={1}>
                      <Box flex={1}>
                        <Button
                          fullWidth
                          variant='cancel'
                          onPress={() => {
                            setUpgradeTeamPlanOpen(false);
                          }}>
                          <Text color='black'>{t('shared:cancel')}</Text>
                        </Button>
                      </Box>

                      <Box flex={1}>
                        <Button
                          fullWidth
                          marginLeft='xs'
                          variant='primary'
                          disabled={
                            !changed() || loading || displayPlan == null
                          }
                          onPress={() => {
                            if (plan === null && displayPlan != null)
                              setPlan(displayPlan);
                            submitForm();
                          }}>
                          {loading ? (
                            <ActivityIndicator />
                          ) : (
                            <Text color='background'>{t('shared:update')}</Text>
                          )}
                        </Button>
                      </Box>
                    </Box>
                  )}
              </Box>
            </Box>
          )}
        </Box>
      </>
    );
  } else {
    return (
      <ScrollView showsVerticalScrollIndicator={false}>
        <Box maxWidth={560} padding='l' marginTop='xxl'>
          <HeaderGoBack
            redirectTo='pick-plans'
            titleHeader='Upgrade'
            params={{
              isPersonal,
              teamId: teamDetail?.id,
              teamUUId: teamDetail?.uuid,
            }}
            noSpacing
          />
          <Box mb='l'>
            <Text variant='labelSmall' mb='xs'>
              Plan Selected
            </Text>
            <Box flexDirection='row' justifyContent='space-between'>
              {filteredProducts?.map((product) => {
                return planItem(product);
              })}
            </Box>
          </Box>

          {teamPlanSelected && (
            <Box mb='l'>
              <Text marginBottom='xs' variant='labelSmall'>
                Team Selected
              </Text>
              <Button
                variant='edit'
                marginBottom='s'
                fullWidth
                onPress={() => navigator.navigate('choose-team')}
                accessibilityLabel='Assign Team'
                disabled={false}>
                {selectedTeamName || currentTeam?.name ? (
                  <Box
                    flexDirection='row'
                    alignItems='flex-end'
                    justifyContent='center'>
                    <Icon name='Logo' height={16} marginRight='xs' />
                    <Text variant='labelSmall' color='textSecondary'>
                      {selectedTeamName ?? currentTeam?.name}
                    </Text>
                  </Box>
                ) : (
                  <Box
                    flexDirection='row'
                    alignItems='flex-end'
                    justifyContent='center'>
                    {name && logo && (
                      <Box mr='xs'>
                        <CachedImage
                          image={logo as unknown as LocalFile}
                          height={16}
                          width={16}
                        />
                      </Box>
                    )}
                    <Text variant='labelSmall' color='textSecondary'>
                      {teamDetail?.id && !name && !isPersonal
                        ? teamDetail?.name
                        : name || t('models:teams.subscriptions.noTeam')}
                    </Text>
                  </Box>
                )}
              </Button>
              <Text marginBottom='xs' variant='labelSmall'>
                {t('models:teams.subscriptions.noTeamMessage')}
              </Text>
            </Box>
          )}
          <Box mb='l'>
            <Text variant='labelSmall' mb='xs'>
              {t('models:teams.subscriptions.billingCycle')}
            </Text>
            <Box flexDirection='row' justifyContent='space-between'>
              <Box flexDirection='row' alignItems='center'>
                <Box mr='xs'>
                  <RadioButton
                    isSelected={billingCycle === 'year'}
                    onPress={() => setBillingCycle('year')}
                  />
                </Box>
                <Box>
                  <Text variant='labelEmphasized'>
                    {t('models:payments.billingCycleLabel', {
                      context: 'year',
                    })}
                  </Text>
                </Box>
              </Box>
              <Box flexDirection='row' alignItems='center'>
                <Box mr='xs'>
                  <RadioButton
                    isSelected={billingCycle === 'month'}
                    onPress={() => setBillingCycle('month')}
                  />
                </Box>
                <Text variant='labelEmphasized'>
                  {t('models:payments.billingCycleLabel', { context: 'month' })}
                </Text>
              </Box>
            </Box>
            {percentageSaved && (
              <Box flexDirection='row' justifyContent='space-between'>
                <Text
                  variant='metadata'
                  color='greenSecondary'
                  style={{ marginStart: theme.spacing.l + theme.spacing.xs }}>
                  {t('models:payments.annualSavings', {
                    savingPercentage: percentageSaved,
                  })}
                </Text>
              </Box>
            )}
          </Box>

          {sortedUsers && sortedUsers.length > 0 && (
            <Box
              flexDirection='column'
              alignItems='flex-start'
              justifyContent='center'
              marginBottom='l'>
              <Text variant='labelSmall' marginBottom='xs'>
                {t('models:teams.subscriptions.paidMembers', {
                  count: sortedUsers.length,
                })}
              </Text>
              <Box
                flex={1}
                flexDirection='row'
                alignItems='flex-start'
                justifyContent='flex-start'
                flexWrap='wrap'>
                <Box justifyContent='center' alignItems='center'>
                  <TouchableOpacity
                    onPress={() => navigator.navigate('add-members')}
                    accessibilityLabel='Add More'>
                    <Box
                      flexDirection='column'
                      alignItems='center'
                      justifyContent='center'
                      width={56}
                      marginRight='m'>
                      <Box
                        alignItems='center'
                        justifyContent='center'
                        borderRadius='xxl'
                        bg='black'
                        width={56}
                        height={56}>
                        <Icon
                          width={40}
                          height={40}
                          name='Plus'
                          color='white'
                          onPress={() => navigator.navigate('add-members')}
                        />
                      </Box>
                      <Text
                        variant='labelSmall'
                        marginTop='xs'
                        textAlign='center'
                        numberOfLines={1}>
                        {t('models:projects.create.members.addMore')}
                      </Text>
                    </Box>
                  </TouchableOpacity>
                </Box>
                {sortedUsers?.map((user) => {
                  if (!user) return;
                  const { id: memberId, firstName, name, avatar, role } = user;

                  return (
                    <Box key={memberId} marginRight='m' alignItems='center'>
                      <Avatar
                        onDismiss={
                          user?.role !== TeamRole.Owner
                            ? () => {
                                const newUsers = values?.users?.filter(
                                  (u) => u?.id !== memberId
                                );
                                setFieldTouched('users');
                                setFieldValue('users', [...newUsers]);
                              }
                            : undefined
                        }
                        size='large'
                        label={name}
                        avatar={avatar}
                      />
                      <Text
                        variant='labelSmall'
                        marginTop='xs'
                        textAlign='center'
                        style={{ width: 56 }}
                        numberOfLines={1}>
                        {firstName}
                      </Text>
                      <Text
                        variant='metadataSecondary'
                        textAlign='center'
                        style={{ width: 56 }}
                        color='grey04'
                        numberOfLines={1}>
                        {role}
                      </Text>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {}
          {!!sortedUsers && sortedUsers.length < 1 && (
            <Box mb='l'>
              <Text marginBottom='xs' variant='labelSmall'>
                {t('models:teams.subscriptions.members')}
              </Text>
              <Button
                variant='edit'
                fullWidth
                marginBottom='s'
                onPress={() => navigator.navigate('add-members')}
                accessibilityLabel='Add Members'>
                {t('models:teams.subscriptions.addMembers')}
              </Button>
            </Box>
          )}

          {listProducts?.length && (
            <Box mb='l'>
              <Text variant='labelSmall' mb='xs'>
                {t('models:teams.subscriptions.subtotal')}
              </Text>
              <Box flexDirection='row'>
                {billingCycle === 'year' && (
                  <Text
                    color='grey04'
                    textDecorationStyle='solid'
                    textDecorationLine='line-through'
                    mr='xxs'>
                    {originalCostLabel}
                  </Text>
                )}
                <Text variant='labelEmphasized'>{subtotalLabel}</Text>
              </Box>
            </Box>
          )}
          {!teamPlanSelected && (
            <Box mb='l' flexDirection='row'>
              <Text variant='body'>
                {t('models:teams.subscriptions.workWithATeam.firstPart')}
              </Text>
              <Pressable onPress={switchToTeamPlan}>
                <Text variant='body' color='greenSecondary'>
                  {t('models:teams.subscriptions.workWithATeam.secondPart')}
                </Text>
              </Pressable>
              <Text variant='body'>
                {t('models:teams.subscriptions.workWithATeam.thirdPart')}
              </Text>
            </Box>
          )}
          <Box height={1} backgroundColor='grey02' mb='l' />
          {/* Payment Form */}
          <Box>
            <Button
              variant='primary'
              testID='submit-button'
              disabled={!isValid || loading}
              onPress={submitForm}>
              {loading ? (
                <ActivityIndicator />
              ) : (
                <Text color='background'>
                  {t('models:teams.subscriptions.startYourTrial')}
                </Text>
              )}
            </Button>
          </Box>
        </Box>
      </ScrollView>
    );
  }
};
